/**
 * ConfirmarTransferenciaPage — UNE-Credito confirms receipt of transfers from FiadoSA.
 * Left: sent transfers pending confirmation + confirmed history
 * Right: transfer detail (desglose by credit)
 */

import { useState, useMemo } from 'react';
import { RefreshCw, CheckCircle, ArrowUpDown, ArrowUp, ArrowDown, Eye } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES, PARTNER_ID, UNE_PERM } from '@/constants';
import { Can } from '@/components/auth/Can';
import { useAuth } from '@/hooks/useAuth';
import {
  getTransfersSent,
  confirmTransferReceipt,
  getTransferHistory,
  getTransferDetail,
} from '@/services/credit-worker.service';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Transferencias', path: ROUTES.COLLECTION_TRANSFERS },
  { label: 'Confirmar', path: '#' },
];

function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
}

interface TransferItem {
  transfer_id: string;
  transfer_date: string;
  total_principal_mxn: number;
  total_partner_interest_mxn: number;
  total_fiado_interest_mxn: number;
  total_amount_mxn: number;
  records_count: number;
  credits_count: number;
  status: string;
  bank_reference?: string;
}

interface TransferDetailItem {
  detail_id: string;
  credit_id: string;
  principal_amount_mxn: number;
  partner_interest_mxn: number;
  total_amount_mxn: number;
}

// ─── Left: Sent List ───────────────────────────────────────────────────────

function SentList({ items, selectedId, onSelect, onConfirm, isConfirming }: {
  items: TransferItem[]; selectedId: string | null;
  onSelect: (item: TransferItem) => void; onConfirm: (item: TransferItem) => void; isConfirming: boolean;
}) {
  if (items.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm text-muted-foreground">Sin transferencias pendientes</p>
        <p className="mt-1 text-xs text-muted-foreground">FiadoSA aun no ha enviado transferencias</p>
      </div>
    );
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="px-3 py-1.5 font-medium">Fecha</th>
          <th className="px-2 py-1.5 text-right font-medium">Monto</th>
          <th className="px-2 py-1.5 font-medium">Ref</th>
          <th className="px-2 py-1.5 w-10" />
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.transfer_id} onClick={() => onSelect(item)}
            className={`cursor-pointer border-b border-border/20 transition-colors ${item.transfer_id === selectedId ? 'bg-primary/5' : 'hover:bg-muted/40'}`}
            style={{ height: 36 }}>
            <td className="px-3 py-0 text-xs text-foreground">{formatDateShort(item.transfer_date)}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs font-medium text-foreground">{formatCurrency(item.total_amount_mxn, 'MXN', 'es-MX')}</td>
            <td className="px-2 py-0 text-xs text-muted-foreground font-mono">{item.bank_reference?.slice(0, 16) ?? '-'}</td>
            <td className="px-2 py-0">
              <Can permission={UNE_PERM.TRANSFER_CONFIRM}>
                <button type="button" onClick={(e) => { e.stopPropagation(); onConfirm(item); }} disabled={isConfirming}
                  className="rounded p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors disabled:opacity-30" title="Confirmar recepcion">
                  <CheckCircle size={16} />
                </button>
              </Can>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Left: History ─────────────────────────────────────────────────────────

function HistoryList({ items, selectedId, onSelect }: {
  items: TransferItem[]; selectedId: string | null; onSelect: (item: TransferItem) => void;
}) {
  if (items.length === 0) return <p className="py-6 text-center text-sm text-muted-foreground">Sin historial</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="px-3 py-1.5 font-medium">Fecha</th>
          <th className="px-2 py-1.5 text-center font-medium">Cred</th>
          <th className="px-2 py-1.5 text-right font-medium">Monto</th>
          <th className="px-2 py-1.5 font-medium">Ref</th>
          <th className="px-2 py-1.5 text-center font-medium">Estado</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const sv = item.status === 'CONFIRMED' || item.status === 'COMPLETED' ? 'success' as const : 'warning' as const;
          const sl = item.status === 'CONFIRMED' || item.status === 'COMPLETED' ? 'Confirmado' : 'Pendiente';
          return (
          <tr key={item.transfer_id} onClick={() => onSelect(item)}
            className={`cursor-pointer border-b border-border/20 transition-colors ${item.transfer_id === selectedId ? 'bg-primary/5' : 'hover:bg-muted/40'}`}
            style={{ height: 32 }}>
            <td className="px-3 py-0 text-xs text-foreground">{formatDateShort(item.transfer_date)}</td>
            <td className="px-2 py-0 text-center tabular-nums text-xs text-foreground">{item.credits_count ?? '-'}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs font-medium text-foreground">{formatCurrency(item.total_amount_mxn, 'MXN', 'es-MX')}</td>
            <td className="px-2 py-0 text-xs text-muted-foreground font-mono">{item.bank_reference?.slice(0, 15) ?? '-'}</td>
            <td className="px-2 py-0 text-center"><Badge variant={sv} className="text-[9px]">{sl}</Badge></td>
          </tr>);
        })}
      </tbody>
    </table>
  );
}

// ─── Sortable Detail Table ────────────────────────────────────────────────

function CouponDetailModal({ creditId, onClose }: { creditId: string; onClose: () => void }) {
  const { data } = useQuery({
    queryKey: ['credit-collections', creditId],
    queryFn: async () => {
      const { getCreditCollectionRecords } = await import('@/services/credit-worker.service');
      return getCreditCollectionRecords(creditId);
    },
  });
  const records: any[] = data?.data ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[80vh] overflow-auto rounded-lg border border-border bg-card shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3 sticky top-0 bg-card">
          <h3 className="text-sm font-semibold text-foreground">Cobros del credito {creditId.slice(0, 8)}...</h3>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-muted text-muted-foreground">✕</button>
        </div>
        {records.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-1.5 font-medium">Cupon</th>
                <th className="px-2 py-1.5 font-medium">Fecha</th>
                <th className="px-2 py-1.5 font-medium">Estado</th>
                <th className="px-2 py-1.5 text-right font-medium">Cobrado</th>
                <th className="px-2 py-1.5 text-right font-medium">Principal</th>
                <th className="px-2 py-1.5 text-right font-medium">Interes</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r: any, i: number) => (
                <tr key={r.record_id ?? i} className="border-b border-border/20" style={{ height: 30 }}>
                  <td className="px-4 py-0 text-xs text-foreground">{r.coupon_number}</td>
                  <td className="px-2 py-0 text-xs text-muted-foreground">{r.collection_date?.split('T')[0] ?? '-'}</td>
                  <td className="px-2 py-0 text-xs"><Badge variant={r.collection_status === 'FULL' ? 'success' : 'warning'} className="text-[9px]">{r.collection_status}</Badge></td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs font-medium">{formatCurrency(r.amount_collected_mxn, 'MXN', 'es-MX')}</td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs">{formatCurrency(r.principal_collected_mxn, 'MXN', 'es-MX')}</td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs">{formatCurrency(r.interest_collected_mxn, 'MXN', 'es-MX')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SortableDetailTable({ details }: { details: TransferDetailItem[] }) {
  type SK = 'credit_id' | 'records_count' | 'principal_amount_mxn' | 'partner_interest_mxn' | 'total_amount_mxn';
  const [sortKey, setSortKey] = useState<SK | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedCreditId, setSelectedCreditId] = useState<string | null>(null);
  const handleSort = (key: SK) => { if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('asc'); } };
  const SortIcon = ({ col }: { col: SK }) => { if (sortKey !== col) return <ArrowUpDown size={10} className="opacity-30" />; return sortDir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />; };
  const sorted = useMemo(() => {
    if (!sortKey) return details;
    return [...details].sort((a, b) => {
      const va = (a as any)[sortKey]; const vb = (b as any)[sortKey];
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va ?? '').localeCompare(String(vb ?? '')) : String(vb ?? '').localeCompare(String(va ?? ''));
    });
  }, [details, sortKey, sortDir]);

  return (
    <>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-1 py-1.5 w-6"></th>
            <th className="px-2 py-1.5 font-medium"><button type="button" className="flex items-center gap-1 hover:text-foreground" onClick={() => handleSort('credit_id')}>Credit ID <SortIcon col="credit_id" /></button></th>
            <th className="px-2 py-1.5 text-right font-medium"><button type="button" className="flex items-center gap-1 justify-end hover:text-foreground ml-auto" onClick={() => handleSort('records_count')}>Cobros <SortIcon col="records_count" /></button></th>
            <th className="px-2 py-1.5 text-right font-medium"><button type="button" className="flex items-center gap-1 justify-end hover:text-foreground ml-auto" onClick={() => handleSort('principal_amount_mxn')}>Principal <SortIcon col="principal_amount_mxn" /></button></th>
            <th className="px-2 py-1.5 text-right font-medium"><button type="button" className="flex items-center gap-1 justify-end hover:text-foreground ml-auto" onClick={() => handleSort('partner_interest_mxn')}>Intereses <SortIcon col="partner_interest_mxn" /></button></th>
            <th className="px-2 py-1.5 text-right font-medium"><button type="button" className="flex items-center gap-1 justify-end hover:text-foreground ml-auto" onClick={() => handleSort('total_amount_mxn')}>Total <SortIcon col="total_amount_mxn" /></button></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((d) => (
            <tr key={d.detail_id ?? d.credit_id} className="border-b border-border/20 hover:bg-muted/40 transition-colors" style={{ height: 32 }}>
              <td className="px-1 py-0 text-center">
                <button type="button" onClick={() => d.credit_id && setSelectedCreditId(d.credit_id)} className="text-muted-foreground hover:text-primary transition-colors" title="Ver cobros">
                  <Eye size={14} />
                </button>
              </td>
              <td className="px-2 py-0 font-mono text-[11px] text-foreground">{d.credit_id?.slice(0, 8) ?? '-'}...</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs text-muted-foreground">{(d as any).records_count ?? (d as any).records ?? '—'}</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs">{formatCurrency(d.principal_amount_mxn, 'MXN', 'es-MX')}</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs">{formatCurrency(d.partner_interest_mxn, 'MXN', 'es-MX')}</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs font-semibold">{formatCurrency(d.total_amount_mxn, 'MXN', 'es-MX')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {selectedCreditId && <CouponDetailModal creditId={selectedCreditId} onClose={() => setSelectedCreditId(null)} />}
    </>
  );
}

// ─── Right: Detail ─────────────────────────────────────────────────────────

function DetailPanel({ transfer, details }: { transfer: TransferItem | null; details: TransferDetailItem[] }) {
  if (!transfer) return <p className="py-12 text-center text-sm text-muted-foreground">Selecciona una transferencia</p>;

  return (
    <div>
      <div className="border-b border-border bg-muted/30 px-3 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Transferencia {formatDateShort(transfer.transfer_date)}
          <span className="ml-2 normal-case tracking-normal font-normal">— {transfer.credits_count} creditos · {formatCurrency(transfer.total_amount_mxn, 'MXN', 'es-MX')}</span>
        </h3>
        {transfer.bank_reference && <p className="mt-1 text-xs text-muted-foreground">Ref: <span className="font-mono text-foreground">{transfer.bank_reference}</span></p>}
      </div>
      <div className="border-b border-border px-3 py-2 grid grid-cols-3 gap-2 text-xs">
        <div><span className="text-muted-foreground">Principal</span><br/><span className="font-semibold">{formatCurrency(transfer.total_principal_mxn, 'MXN', 'es-MX')}</span></div>
        <div><span className="text-muted-foreground">Intereses</span><br/><span className="font-semibold">{formatCurrency(transfer.total_partner_interest_mxn, 'MXN', 'es-MX')}</span></div>
        <div><span className="text-muted-foreground">Total</span><br/><span className="font-bold">{formatCurrency(transfer.total_amount_mxn, 'MXN', 'es-MX')}</span></div>
      </div>
      {details.length > 0 ? (
        <SortableDetailTable details={details} />
      ) : (
        <p className="py-8 text-center text-sm text-muted-foreground">Sin detalle disponible</p>
      )}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function ConfirmarTransferenciaPage() {
  const { user } = useAuth();

  const { data: sentData, refetch: refetchSent } = useQuery({
    queryKey: ['transfers', 'sent'],
    queryFn: () => getTransfersSent(PARTNER_ID),
  });

  const { data: historyData, refetch: refetchHistory } = useQuery({
    queryKey: ['transfers', 'history-confirmed'],
    queryFn: () => getTransferHistory(PARTNER_ID),
  });

  const sentTransfers: TransferItem[] = sentData?.data ?? [];
  const confirmedHistory: TransferItem[] = (historyData?.data ?? []).filter((t: TransferItem) => t.status === 'CONFIRMED' || t.status === 'COMPLETED');

  const [selectedTransfer, setSelectedTransfer] = useState<TransferItem | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // Fetch detail when a transfer is selected
  const { data: detailData } = useQuery({
    queryKey: ['transfers', 'detail', selectedTransfer?.transfer_id],
    queryFn: () => selectedTransfer ? getTransferDetail(selectedTransfer.transfer_id, PARTNER_ID) : Promise.resolve(null),
    enabled: !!selectedTransfer,
  });

  const details: TransferDetailItem[] = (detailData?.data?.details ?? []) as unknown as TransferDetailItem[];

  const handleConfirmErrorMessage = (error: unknown): string => {
    if (isAxiosError(error)) {
      const code = error.response?.data?.code;
      const description = error.response?.data?.description ?? error.response?.data?.msg;
      if (code === 'TRANSFER_NOT_SENT') return 'Solo se puede confirmar una transferencia en status SENT.';
      if (code === 'TRANSFER_NOT_FOUND') return 'Transferencia no encontrada.';
      return description ?? error.message ?? 'Error inesperado';
    }
    return 'Error inesperado';
  };

  const handleConfirm = async (item: TransferItem) => {
    setIsConfirming(true);
    try {
      await confirmTransferReceipt(item.transfer_id, { partnerId: PARTNER_ID, confirmedBy: user?.email ?? 'admin' });
      await Promise.all([refetchSent(), refetchHistory()]);
      toast.success('Transferencia confirmada');
      setSelectedTransfer(null);
    } catch (error) {
      toast.error(`No se pudo confirmar: ${handleConfirmErrorMessage(error)}`);
    } finally {
      setIsConfirming(false);
    }
  };

  const refetchAll = () => { void refetchSent(); void refetchHistory(); };

  return (
    <div>
      <PageHeader title="Confirmar Cobros" helpKey="confirmar-cobros" subtitle="Confirmar recepcion de cobros enviados por FiadoSA" breadcrumbs={BREADCRUMBS}
        actions={<Button onClick={refetchAll}><RefreshCw size={16} /> Actualizar</Button>} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[500px_1fr]">
        <div className="space-y-4">
          <div className={`rounded-lg border-2 overflow-hidden ${
            sentTransfers.length > 0
              ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20'
              : 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
          }`}>
            <div className={`border-b px-3 py-2 ${
              sentTransfers.length > 0
                ? 'border-amber-200 dark:border-amber-800 bg-amber-100/50 dark:bg-amber-900/30'
                : 'border-green-200 dark:border-green-800 bg-green-100/50 dark:bg-green-900/30'
            }`}>
              <h3 className={`text-xs font-semibold uppercase tracking-wider ${
                sentTransfers.length > 0
                  ? 'text-amber-700 dark:text-amber-400'
                  : 'text-green-700 dark:text-green-400'
              }`}>
                {sentTransfers.length > 0 ? `Por confirmar (${sentTransfers.length})` : 'Todo confirmado ✓'}
              </h3>
            </div>
            <SentList items={sentTransfers} selectedId={selectedTransfer?.transfer_id ?? null} onSelect={setSelectedTransfer} onConfirm={handleConfirm} isConfirming={isConfirming} />
          </div>
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="border-b border-border bg-muted/30 px-3 py-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Confirmadas</h3>
            </div>
            <HistoryList items={confirmedHistory} selectedId={selectedTransfer?.transfer_id ?? null} onSelect={setSelectedTransfer} />
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <DetailPanel transfer={selectedTransfer} details={details} />
        </div>
      </div>
    </div>
  );
}
